import { FC } from 'react';

import { AddDocumentIcon } from '../../../assets/icons/index';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import Button from '../../../components/button/index';
import { CloseModalButton } from '../../../components/close-modal-button/index';
import { Sheet, SheetContent } from '../../../components/sheet/index';
import { DividendPayoutRow } from '../../../hooks/dividend/use-dividend-payout-rows.hook';
import { useFormat } from '../../../hooks/use-format.hook';
import { getS3FileOriginalName } from '../../../utils/get-s3file-original-name.util';
import { getTranslation } from '../../../utils/get-translation.util';
import { SummaryRow, SummarySection } from '../add-dividend/dividend-summary-parts';
import { formatDividendAmount, formatDividendPerShare } from '../utils/format-dividend-amount.util';
import { DividendStatusTag } from './dividend-status-tag';

type Props = {
  currencySymbol: string;
  isOpen: boolean;
  onClose: () => void;
  onDocumentClick: (link: string) => void;
  period: string;
  row: DividendPayoutRow | null;
};

const EMPTY_VALUE = '—';

const formatNumber = (value: number): string => formatDividendPerShare(value);

/** Per-stakeholder view of one dividend payout row — opened from the row actions menu. */
export const DividendStakeholderDetailsSheet: FC<Props> = ({
  currencySymbol,
  isOpen,
  onClose,
  onDocumentClick,
  period,
  row,
}) => {
  const { format } = useFormat();

  if (!row) return null;

  const filesLinks = row.filesLinks ?? [];
  const formatDate = (value: null | string) =>
    value ? format(new Date(value), 'dd/MM/yyyy') : EMPTY_VALUE;

  return (
    <Sheet onOpenChange={(open) => !open && onClose()} open={isOpen}>
      <SheetContent
        className="w-full max-w-[485px] border-transparent bg-transparent p-2 shadow-none"
        onInteractOutside={onClose}
        side="RIGHT"
      >
        <div className="flex h-full w-full flex-col overflow-hidden rounded-lg border border-gray-300 bg-gray-100 pb-4">
          <div className="flex h-[72px] w-full items-center justify-between gap-3 bg-white px-6">
            <div className="flex min-w-0 flex-col">
              <span
                className="truncate text-xl font-semibold leading-8 text-gray-700"
                title={row.stakeholderName}
              >
                {row.stakeholderName}
              </span>
              <span className="truncate text-xs font-[450] leading-4 text-gray-500">
                {[row.payoutName, period].filter(Boolean).join(' · ')}
              </span>
            </div>
            <CloseModalButton onClose={onClose} />
          </div>

          <div className="flex h-full flex-col gap-4 overflow-hidden">
            <div className="invisible-y-scroll flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto rounded-b-xl border-b border-gray-300 bg-white px-6 pb-6 pt-4">
              <SummarySection title={getTranslation('GENERAL')}>
                <SummaryRow label={getTranslation('PAYOUT_NAME')} value={row.payoutName} />
                <SummaryRow label={getTranslation('PERIOD_COVERED')} value={period} />
                <SummaryRow
                  label={getTranslation('STATUS')}
                  value={<DividendStatusTag status={row.status} />}
                />
                <SummaryRow
                  label={getTranslation('APPROVAL_DATE')}
                  value={formatDate(row.approvalDate)}
                />
                <SummaryRow
                  label={getTranslation('PAYOUT_DATE')}
                  value={formatDate(row.payoutDate)}
                />
              </SummarySection>

              <SummarySection strong title={row.shareClassName ?? EMPTY_VALUE}>
                <SummaryRow
                  label={getTranslation('DIVIDEND_PER_SHARE')}
                  value={
                    row.dividendPerShare === null
                      ? EMPTY_VALUE
                      : `${currencySymbol} ${formatNumber(row.dividendPerShare)}`
                  }
                />
                <SummaryRow
                  label={getTranslation('NUMBER_OF_SHARES')}
                  value={row.shares === null ? EMPTY_VALUE : formatNumber(row.shares)}
                />
                <SummaryRow
                  highlight
                  label={getTranslation('TOTAL_DIVIDEND_PAYOUT')}
                  value={`${currencySymbol} ${formatDividendAmount(row.totalAmount)}`}
                />
              </SummarySection>

              <SummarySection strong title={getTranslation('DOCUMENTS')}>
                {filesLinks.length === 0 ? (
                  <SummaryRow
                    hideColon
                    label={getTranslation('ATTACHED_FILES_TITLE')}
                    value={<AppFormattedMessage id="NO_DOCUMENTS" />}
                  />
                ) : (
                  <SummaryRow
                    hideColon
                    label={getTranslation('ATTACHED_FILES_TITLE')}
                    value={
                      <span className="flex flex-col items-start gap-2">
                        {filesLinks.map((link) => (
                          <button
                            className="flex w-full items-center gap-2 text-left"
                            key={link}
                            onClick={() => onDocumentClick(link)}
                            type="button"
                          >
                            <AddDocumentIcon className="size-4 shrink-0" />
                            <span className="min-w-0 flex-1 truncate text-brand-700 underline">
                              {getS3FileOriginalName(link)}
                            </span>
                          </button>
                        ))}
                      </span>
                    }
                  />
                )}
              </SummarySection>
            </div>

            <div className="flex w-full shrink-0 items-center justify-end px-6">
              <Button
                className="h-9 w-fit px-4 py-2.5 text-sm font-[550] text-white"
                onClick={onClose}
              >
                <AppFormattedMessage id="CLOSE" />
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
